/**
 * FeatureFlagContext - Centralized Feature Flag State Management
 * 
 * This context provides a single source of truth for all feature flags across the application.
 * It fetches feature flags from the backend when the app loads and makes them available
 * to all components via React Context.
 * 
 * Benefits:
 * - Single API call for all flags
 * - Instant global updates when flags change
 * - No duplicate fetch logic in components
 * - Consistent flag status across the app
 * 
 * Usage:
 * ```jsx
 * import { useFeatureFlags } from '../context/FeatureFlagContext';
 * 
 * function MyComponent() {
 *   const { flags, loading, error, refreshFlags } = useFeatureFlags();
 *   
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   
 *   // Check if a feature is enabled
 *   if (flags.venues?.enabled) {
 *     return <VenuesFeature />;
 *   }
 *   
 *   // Check if a feature is coming soon
 *   if (flags.airports?.coming_soon) {
 *     return <ComingSoonNotice />;
 *   }
 *   
 *   return null;
 * }
 * ```
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create the context
const FeatureFlagContext = createContext(null);

/**
 * Feature Flag Provider Component
 * Wraps the application and provides feature flag state to all children
 */
export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  /**
   * Fetch all feature flags from the backend
   * Combines standard feature flags and special flags (like green room)
   */
  const fetchFeatureFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const flagsObject = {};
      
      // Fetch standard feature flags
      try {
        console.log('[FeatureFlagContext] Fetching standard feature flags...');
        const response = await axios.get(`${API}/feature-flags/public`);
        
        if (Array.isArray(response.data)) {
          response.data.forEach(flag => {
            flagsObject[flag.feature_id] = {
              feature_id: flag.feature_id,
              enabled: flag.enabled === true,
              disabled: flag.disabled === true,
              coming_soon: flag.coming_soon === true,
              name: flag.name || flag.feature_id,
              description: flag.description || '',
              loading: false
            };
          });
          console.log(`[FeatureFlagContext] Loaded ${response.data.length} standard flags`);
        }
      } catch (err) {
        console.error('[FeatureFlagContext] Error fetching standard flags:', err);
      }
      
      // Fetch The Green Room feature flag separately (special endpoint)
      try {
        console.log('[FeatureFlagContext] Fetching Green Room flag...');
        const greenRoomResponse = await axios.get(`${API}/features/the-green-room/status`);
        const status = greenRoomResponse.data.status;
        
        flagsObject['the_green_room'] = {
          feature_id: 'the_green_room',
          enabled: status === 'enabled',
          disabled: status === 'disabled',
          coming_soon: status === 'coming_soon',
          name: 'The Driver Bay',
          description: 'Members-only classifieds marketplace',
          loading: false
        };
        console.log('[FeatureFlagContext] Green Room flag loaded:', status);
      } catch (err) {
        console.error('[FeatureFlagContext] Error fetching Green Room flag:', err);
        // Default to enabled if fetch fails (graceful degradation)
        flagsObject['the_green_room'] = { 
          feature_id: 'the_green_room',
          enabled: true, 
          disabled: false,
          coming_soon: false,
          name: 'The Driver Bay',
          description: 'Members-only classifieds marketplace',
          loading: false 
        };
      }
      
      console.log('[FeatureFlagContext] All flags loaded:', Object.keys(flagsObject));
      setFlags(flagsObject);
      setLastFetch(new Date());
      
    } catch (err) {
      console.error('[FeatureFlagContext] Critical error fetching feature flags:', err);
      setError(err.message || 'Failed to load feature flags');
      
      // Set default flags to prevent app breakage
      setFlags({
        venues: { feature_id: 'venues', enabled: false, coming_soon: true, loading: false },
        airports: { feature_id: 'airports', enabled: false, coming_soon: true, loading: false },
        master_tour: { feature_id: 'master_tour', enabled: false, coming_soon: true, loading: false },
        analytics: { feature_id: 'analytics', enabled: false, coming_soon: true, loading: false },
        mobile_app: { feature_id: 'mobile_app', enabled: false, coming_soon: true, loading: false },
        the_green_room: { feature_id: 'the_green_room', enabled: true, disabled: false, coming_soon: false, loading: false }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch flags on mount and poll every 30s for admin changes
  useEffect(() => {
    console.log('[FeatureFlagContext] Initializing...');
    fetchFeatureFlags();
    const interval = setInterval(() => {
      fetchFeatureFlags();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchFeatureFlags]);

  /**
   * Refresh feature flags (useful after admin updates)
   */
  const refreshFlags = useCallback(() => {
    console.log('[FeatureFlagContext] Manual refresh triggered');
    return fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  /**
   * Check if a specific feature is enabled
   * @param {string} featureId - Feature identifier (e.g., 'venues', 'the_green_room')
   * @returns {boolean} True if feature is enabled
   */
  const isFeatureEnabled = useCallback((featureId) => {
    const flag = flags[featureId];
    return flag?.enabled === true;
  }, [flags]);

  /**
   * Check if a specific feature is coming soon
   * @param {string} featureId - Feature identifier
   * @returns {boolean} True if feature is coming soon
   */
  const isFeatureComingSoon = useCallback((featureId) => {
    const flag = flags[featureId];
    return flag?.coming_soon === true;
  }, [flags]);

  /**
   * Check if a specific feature is disabled
   * @param {string} featureId - Feature identifier
   * @returns {boolean} True if feature is disabled
   */
  const isFeatureDisabled = useCallback((featureId) => {
    const flag = flags[featureId];
    return flag?.disabled === true || (!flag?.enabled && !flag?.coming_soon);
  }, [flags]);

  /**
   * Get feature status
   * @param {string} featureId - Feature identifier
   * @returns {string} 'enabled', 'disabled', or 'coming_soon'
   */
  const getFeatureStatus = useCallback((featureId) => {
    const flag = flags[featureId];
    if (flag?.enabled) return 'enabled';
    if (flag?.coming_soon) return 'coming_soon';
    if (flag?.disabled) return 'disabled';
    return 'disabled'; // default
  }, [flags]);

  /**
   * Update a feature flag and sync with backend
   * @param {string} featureId - Feature identifier
   * @param {object} updates - Updates to apply (enabled, disabled, coming_soon)
   */
  const updateFlag = useCallback(async (featureId, updates) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Update backend first
      await axios.put(
        `${API}/admin/feature-flags/${featureId}`,
        updates,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Then update local state immediately for responsiveness
      setFlags(prevFlags => ({
        ...prevFlags,
        [featureId]: {
          ...prevFlags[featureId],
          ...updates,
          feature_id: featureId,
          loading: false
        }
      }));

      console.log(`[FeatureFlagContext] Flag ${featureId} updated:`, updates);
      return true;
    } catch (error) {
      console.error(`[FeatureFlagContext] Failed to update flag ${featureId}:`, error);
      // Refresh from backend to ensure consistency
      await fetchFeatureFlags();
      throw error;
    }
  }, [fetchFeatureFlags]);

  const value = {
    flags,
    loading,
    error,
    lastFetch,
    refreshFlags,
    isFeatureEnabled,
    isFeatureComingSoon,
    isFeatureDisabled,
    getFeatureStatus,
    updateFlag
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Custom hook to access feature flags
 * @returns {object} Feature flag context value
 * @throws {Error} If used outside FeatureFlagProvider
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  
  if (context === null) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  
  return context;
};

/**
 * HOC to wrap components that need feature flags
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with feature flags
 */
export const withFeatureFlags = (Component) => {
  return (props) => {
    const featureFlags = useFeatureFlags();
    return <Component {...props} featureFlags={featureFlags} />;
  };
};

export default FeatureFlagContext;
